import { createAsyncThunk, createSlice, type PayloadAction } from '@reduxjs/toolkit';

import { authApi } from '@/api/auth.api';

import { clearAuthStorage, setAuthTokens } from '@/api/client';
import { clearLastAllowedPath, seedLastAllowedPath } from '@/utils/routeAccess';
import { getPostLoginPath } from '@/utils/moduleScope';
import { enabledHeadModuleCodes } from '@/config/moduleEntries';

import { getOrCreateDeviceId } from '@/core/auth/deviceId';

import { STORAGE_KEYS } from '@/constants/storage';

import type { ErpModule } from '@/api/platform.api';
import { isOrgOwner } from '@/utils/moduleAccess';

import type { AuthUser, LoginRequest, LoginResponse } from '@/types/auth';

import {
  clearEntryModuleStorage,
  persistEntryModule,
  persistWorkspaceModule,
  readEntryModuleFromToken,
  readStoredEntryModule,
  readStoredWorkspaceModule,
  resolveDefaultWorkspaceModule,
} from '@/utils/moduleScope';
import { platformApi } from '@/api/platform.api';

interface AuthState {
  user: AuthUser | null;
  accessToken: string | null;
  refreshToken: string | null;
  enabledFeatures: string[];
  enabledModules: string[];
  accessibleModules: string[];
  entryModule: ErpModule | null;
  /** Active sidebar/workspace — used for org owners (JWT may be unscoped). */
  workspaceModule: ErpModule | null;
  isAuthenticated: boolean;
  loading: boolean;
  initialized: boolean;
}

function loginToUser(data: LoginResponse): AuthUser {
  return {
    userId: data.userId,
    userUuid: data.userUuid,
    email: data.email,
    firstName: data.firstName,
    lastName: data.lastName,
    organizationId: data.organizationId,
    organizationName: data.organizationName ?? null,
    masterAdmin: data.masterAdmin,
    permissions: data.permissions ?? [],
    roleCode: data.roleCode ?? null,
    storeId: data.storeId ?? null,
  };
}

function persistAuthExtras(data: LoginResponse) {
  if (data.enabledFeatures?.length) {
    localStorage.setItem(STORAGE_KEYS.ENABLED_FEATURES, JSON.stringify(data.enabledFeatures));
  }
  if (data.enabledModules?.length) {
    localStorage.setItem('fattah_enabled_modules', JSON.stringify(data.enabledModules));
  }
  if (data.accessibleModules?.length) {
    localStorage.setItem('fattah_accessible_modules', JSON.stringify(data.accessibleModules));
  }
}

function resolveEntryModule(data: LoginResponse, accessToken: string | null): ErpModule | null {
  if (data.entryModule) return data.entryModule as ErpModule;
  return readEntryModuleFromToken(accessToken);
}

function loadStoredAuth(): Pick<
  AuthState,
  | 'user'
  | 'accessToken'
  | 'refreshToken'
  | 'isAuthenticated'
  | 'enabledFeatures'
  | 'enabledModules'
  | 'accessibleModules'
  | 'entryModule'
  | 'workspaceModule'
> {
  const accessToken = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
  const refreshToken = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
  const userRaw = localStorage.getItem('saaspos_user');
  const featuresRaw = localStorage.getItem(STORAGE_KEYS.ENABLED_FEATURES);
  const modulesRaw = localStorage.getItem('fattah_enabled_modules');
  const accessibleRaw = localStorage.getItem('fattah_accessible_modules');

  if (!accessToken || !userRaw) {
    return {
      user: null,
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,
      enabledFeatures: [],
      enabledModules: [],
      accessibleModules: [],
      entryModule: null,
      workspaceModule: null,
    };
  }

  try {
    const user = JSON.parse(userRaw) as AuthUser;
    const entryFromToken = readEntryModuleFromToken(accessToken) ?? readStoredEntryModule();
    const entryModule =
      user.masterAdmin || isOrgOwner(user.permissions, user.roleCode) ? null : entryFromToken;
    const modules = modulesRaw ? (JSON.parse(modulesRaw) as string[]) : [];
    const workspaceModule = user.masterAdmin
      ? null
      : readStoredWorkspaceModule() ??
        entryModule ??
        resolveDefaultWorkspaceModule(modules);
    return {
      user,
      accessToken,
      refreshToken,
      isAuthenticated: true,
      enabledFeatures: featuresRaw ? (JSON.parse(featuresRaw) as string[]) : [],
      enabledModules: modules,
      accessibleModules: accessibleRaw ? (JSON.parse(accessibleRaw) as string[]) : [],
      entryModule,
      workspaceModule,
    };
  } catch {
    return {
      user: null,
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,
      enabledFeatures: [],
      enabledModules: [],
      accessibleModules: [],
      entryModule: null,
      workspaceModule: null,
    };
  }
}

const stored = loadStoredAuth();

const initialState: AuthState = {
  ...stored,
  loading: false,
  initialized: false,
};

function applyLoginPayload(state: AuthState, data: LoginResponse) {
  state.user = loginToUser(data);
  state.accessToken = data.accessToken;
  state.refreshToken = data.refreshToken;
  state.isAuthenticated = true;
  state.enabledFeatures = data.enabledFeatures ?? [];
  state.enabledModules = enabledHeadModuleCodes(data.enabledModules ?? []);
  state.accessibleModules = data.accessibleModules ?? [];
  if (data.masterAdmin) {
    state.entryModule = null;
    state.workspaceModule = null;
    setAuthTokens(data.accessToken, data.refreshToken);
    localStorage.setItem('saaspos_user', JSON.stringify(state.user));
    persistAuthExtras(data);
    clearEntryModuleStorage();
    sessionStorage.removeItem(STORAGE_KEYS.PENDING_ENTRY_MODULE);
    seedLastAllowedPath('/master');
    return;
  }
  const owner = state.user && isOrgOwner(state.user.permissions, state.user.roleCode);
  const resolvedEntry = resolveEntryModule(data, data.accessToken);
  state.entryModule = owner ? null : resolvedEntry;
  const pendingRaw = sessionStorage.getItem(STORAGE_KEYS.PENDING_ENTRY_MODULE);
  const pendingModule = pendingRaw ? (pendingRaw as ErpModule) : null;
  state.workspaceModule =
    (owner ? pendingModule ?? resolvedEntry : resolvedEntry) ??
    resolveDefaultWorkspaceModule(state.enabledModules);
  setAuthTokens(data.accessToken, data.refreshToken);
  localStorage.setItem('saaspos_user', JSON.stringify(state.user));
  persistAuthExtras(data);
  persistEntryModule(state.entryModule);
  persistWorkspaceModule(state.workspaceModule);
  sessionStorage.removeItem(STORAGE_KEYS.PENDING_ENTRY_MODULE);
  const landing = getPostLoginPath(state.workspaceModule ?? state.entryModule, '/org');
  seedLastAllowedPath(landing);
}

export const login = createAsyncThunk('auth/login', async (credentials: Omit<LoginRequest, 'deviceId'>) => {
  const data = await authApi.login({
    ...credentials,
    deviceId: getOrCreateDeviceId(),
  });
  return data;
});

export const verifyMfaLogin = createAsyncThunk(
  'auth/verifyMfa',
  async (payload: { mfaToken: string; totpCode: string; entryModule?: string }) => {
    return authApi.verifyMfa({
      mfaToken: payload.mfaToken,
      totpCode: payload.totpCode,
      deviceId: getOrCreateDeviceId(),
      entryModule: payload.entryModule,
    });
  },
);

export const completeSsoLogin = createAsyncThunk(
  'auth/ssoExchange',
  async (payload: { exchangeToken: string; deviceId?: string; entryModule?: string }) => {
    return authApi.ssoExchange({
      exchangeToken: payload.exchangeToken,
      deviceId: payload.deviceId ?? getOrCreateDeviceId(),
      entryModule: payload.entryModule,
    });
  },
);

export const switchWorkspace = createAsyncThunk(
  'auth/switchWorkspace',
  async (module: ErpModule) => {
    const data = await platformApi.switchWorkspace(module, getOrCreateDeviceId());
    return { data, module };
  },
);

export const logout = createAsyncThunk('auth/logout', async () => {
  try {
    await authApi.logout();
  } finally {
    clearAuthStorage();
    clearEntryModuleStorage();
    clearLastAllowedPath();
    localStorage.removeItem('saaspos_user');
    localStorage.removeItem(STORAGE_KEYS.ENABLED_FEATURES);
    localStorage.removeItem('fattah_enabled_modules');
    localStorage.removeItem('fattah_accessible_modules');
  }
});

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setInitialized: (state) => {
      state.initialized = true;
    },
    hydrateFromLogin: (state, action: PayloadAction<LoginResponse>) => {
      applyLoginPayload(state, action.payload);
    },
    clearAuth: (state) => {
      state.user = null;
      state.accessToken = null;
      state.refreshToken = null;
      state.isAuthenticated = false;
      state.enabledFeatures = [];
      state.enabledModules = [];
      state.accessibleModules = [];
      state.entryModule = null;
      state.workspaceModule = null;
      clearAuthStorage();
      clearEntryModuleStorage();
      clearLastAllowedPath();
      localStorage.removeItem('saaspos_user');
      localStorage.removeItem(STORAGE_KEYS.ENABLED_FEATURES);
      localStorage.removeItem('fattah_enabled_modules');
      localStorage.removeItem('fattah_accessible_modules');
    },
    setEnabledModules: (state, action: PayloadAction<string[]>) => {
      state.enabledModules = action.payload;
      localStorage.setItem('fattah_enabled_modules', JSON.stringify(action.payload));
    },
    setAccessibleModules: (state, action: PayloadAction<string[]>) => {
      state.accessibleModules = action.payload;
      localStorage.setItem('fattah_accessible_modules', JSON.stringify(action.payload));
    },
    /** Updates active workspace UI (sidebar, launcher). */
    setWorkspaceModule: (state, action: PayloadAction<ErpModule>) => {
      state.workspaceModule = action.payload;
      persistWorkspaceModule(action.payload);
      const owner = isOrgOwner(state.user?.permissions ?? [], state.user?.roleCode);
      if (!owner) {
        state.entryModule = action.payload;
        persistEntryModule(action.payload);
      }
    },
    /** @deprecated Use setWorkspaceModule */
    setActiveModule: (state, action: PayloadAction<ErpModule>) => {
      state.workspaceModule = action.payload;
      persistWorkspaceModule(action.payload);
      const owner = isOrgOwner(state.user?.permissions ?? [], state.user?.roleCode);
      if (!owner) {
        state.entryModule = action.payload;
        persistEntryModule(action.payload);
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(login.pending, (state) => {
        state.loading = true;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.loading = false;
        if (!action.payload.mfaRequired) {
          applyLoginPayload(state, action.payload);
        }
      })
      .addCase(verifyMfaLogin.pending, (state) => {
        state.loading = true;
      })
      .addCase(verifyMfaLogin.fulfilled, (state, action) => {
        state.loading = false;
        applyLoginPayload(state, action.payload);
      })
      .addCase(verifyMfaLogin.rejected, (state) => {
        state.loading = false;
      })
      .addCase(completeSsoLogin.fulfilled, (state, action) => {
        applyLoginPayload(state, action.payload);
      })
      .addCase(switchWorkspace.fulfilled, (state, action) => {
        state.loading = false;
        applyLoginPayload(state, action.payload.data);
        state.workspaceModule = action.payload.module;
        persistWorkspaceModule(action.payload.module);
      })
      .addCase(switchWorkspace.pending, (state) => {
        state.loading = true;
      })
      .addCase(switchWorkspace.rejected, (state) => {
        state.loading = false;
      })
      .addCase(login.rejected, (state) => {
        state.loading = false;
      })
      .addCase(logout.fulfilled, (state) => {
        state.user = null;
        state.accessToken = null;
        state.refreshToken = null;
        state.isAuthenticated = false;
        state.enabledFeatures = [];
        state.enabledModules = [];
        state.accessibleModules = [];
        state.entryModule = null;
        state.workspaceModule = null;
      });
  },
});

export const {
  setInitialized,
  hydrateFromLogin,
  clearAuth,
  setEnabledModules,
  setAccessibleModules,
  setWorkspaceModule,
  setActiveModule,
} = authSlice.actions;
export default authSlice.reducer;
